import { useMemo, useState } from 'react'
import { Upload } from 'lucide-react'
import { Button, Drawer, Input, Select, Textarea, toast } from '@/components/ui'
import { useUploadProductDocument } from '../api/queries'
import type { DocumentType } from '../api/types'

export interface UploadDocumentDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  productId: number
  productName: string
  types: DocumentType[]
  /** Pre-select a type (Replace / upload a missing required type). */
  initialTypeKey?: string | null
}

const MAX_BYTES = 25 * 1024 * 1024

/**
 * Upload one PDF as the new current document of a type. The previous
 * current document of that type is kept as history (superseded), and the
 * Odoo Regulatory tab link is updated by the middleware.
 */
export function UploadDocumentDrawer({
  open,
  onOpenChange,
  productId,
  productName,
  types,
  initialTypeKey,
}: UploadDocumentDrawerProps) {
  const upload = useUploadProductDocument()
  // Fresh state per opening: the parent remounts this drawer with a new `key`.
  const [typeKey, setTypeKey] = useState(initialTypeKey ?? '')
  const [file, setFile] = useState<File | null>(null)
  const [versionLabel, setVersionLabel] = useState('')
  const [issuedOn, setIssuedOn] = useState('')
  const [expiresOn, setExpiresOn] = useState('')
  const [notes, setNotes] = useState('')

  const selectedType = useMemo(() => types.find((t) => t.key === typeKey) ?? null, [types, typeKey])

  const fileError =
    file && !file.name.toLowerCase().endsWith('.pdf') && file.type !== 'application/pdf'
      ? 'Only PDF documents are accepted.'
      : file && file.size > MAX_BYTES
        ? 'File is larger than 25 MB.'
        : null
  const dateError = issuedOn && expiresOn && expiresOn < issuedOn ? 'Expiry is before the issue date.' : null
  const canSubmit = !!typeKey && !!file && !fileError && !dateError && !upload.isPending

  const submit = () => {
    if (!file || !canSubmit) return
    upload.mutate(
      {
        productId,
        file,
        docTypeKey: typeKey,
        versionLabel: versionLabel.trim() || undefined,
        issuedOn: issuedOn || undefined,
        expiresOn: expiresOn || undefined,
        notes: notes.trim() || undefined,
      },
      {
        onSuccess: (res) => {
          if (res.unchanged) {
            toast.info('That file is already the current document of this type')
          } else {
            toast.success(`${selectedType?.label ?? 'Document'} uploaded`, {
              description: res.superseded_ids.length ? 'The previous version is kept in history.' : undefined,
            })
          }
          onOpenChange(false)
        },
        onError: (err) => toast.danger('Upload failed', { description: (err as Error).message }),
      },
    )
  }

  return (
    <Drawer
      surface="form"
      open={open}
      onOpenChange={onOpenChange}
      title="Upload document"
      meta={productName}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} disabled={!canSubmit} loading={upload.isPending}>
            <Upload size={14} /> Upload
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 p-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-md font-semibold text-primary">Document type</span>
          <Select aria-label="Document type" value={typeKey} onChange={(e) => setTypeKey(e.target.value)}>
            <option value="">Choose a type…</option>
            {types.map((t) => (
              <option key={t.key} value={t.key}>
                {t.label}
                {t.required_for_onboarding ? ' (required)' : ''}
              </option>
            ))}
          </Select>
          {selectedType?.description ? (
            <span className="text-sm text-secondary">{selectedType.description}</span>
          ) : null}
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-md font-semibold text-primary">PDF file</span>
          <Input
            type="file"
            accept="application/pdf,.pdf"
            aria-label="PDF file"
            invalid={!!fileError}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          {fileError ? <span className="text-sm text-danger-text">{fileError}</span> : null}
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-md font-semibold text-primary">Version / revision (optional)</span>
          <Input
            aria-label="Version"
            value={versionLabel}
            maxLength={64}
            placeholder="e.g. Rev 4"
            onChange={(e) => setVersionLabel(e.target.value)}
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-md font-semibold text-primary">Issued on</span>
            <Input type="date" aria-label="Issued on" value={issuedOn} onChange={(e) => setIssuedOn(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-md font-semibold text-primary">Expires on</span>
            <Input
              type="date"
              aria-label="Expires on"
              value={expiresOn}
              invalid={!!dateError}
              onChange={(e) => setExpiresOn(e.target.value)}
            />
          </label>
        </div>
        {dateError ? <span className="text-sm text-danger-text">{dateError}</span> : null}
        {selectedType?.has_expiry && selectedType.default_validity_months && !expiresOn ? (
          <span className="text-sm text-secondary">
            Leave expiry blank to use the default: {selectedType.default_validity_months} months after the
            issue date.
          </span>
        ) : null}

        <label className="flex flex-col gap-1.5">
          <span className="text-md font-semibold text-primary">Notes (optional)</span>
          <Textarea aria-label="Notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
      </div>
    </Drawer>
  )
}
